import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  confirmCoupon,
  getGiftCouponById,
  shareCoupon,
  unshareCoupon,
  updateCouponRestrictedPhone,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DuplicateRecipientCouponEntry,
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  confirmDelivery,
  getGiftDeliveryById,
  shareGiftDelivery,
  unshareGiftDelivery,
  updateDeliveryRestrictedPhone,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  DuplicateRecipientDeliveryEntry,
  GiftDeliveryForStore,
  GiftDeliveryShareChannel,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { giftOrderForStoreDataAtomFamily } from '@shared/api/giftOrder/giftOrder.atom';
import { updateGiftOrderCourtesy } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderForStore,
  GiftStoreRole,
} from '@shared/api/giftOrder/giftOrder.interface';
import { toastError } from '@shared/plugIn/toast';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useQueryClient } from 'react-query';

import {
  loadPendingConfirmForOrder,
  loadPendingDuplicateSuspicionForOrder,
  loadPendingDuplicateSuspicions,
  loadPendingDuplicateSuspicionsForOrder,
  PendingDuplicateSuspicionEntry,
  removePendingConfirm,
  removePendingDuplicateSuspicion,
  savePendingConfirm,
  savePendingDuplicateSuspicion,
  SHARE_GUIDE_DISMISSED_KEY,
} from '../utils/giftOrderCoupon.utils';
import {
  DuplicateRecipientConflict,
  getDuplicateRecipientConflictsForCoupon,
  getDuplicateRecipientConflictsForDelivery,
} from '../utils/kakaoDuplicateRecipient';
import { PendingConfirmItem } from './giftShareTypes';
import { useGiftMemoModal } from './useGiftMemoModal';
import { useGiftOrderShareItems } from './useGiftOrderShareItems';
import { useGiftPhoneLockEditModal } from './useGiftPhoneLockEditModal';
import { useKakaoShareWebhookCheck } from './useKakaoShareWebhookCheck';

// 쿠폰의 ShareChannel 과 배송의 GiftDeliveryShareChannel 은 enum 정의는 분리되어 있지만
// 실제 string 값은 1:1 동일 ('KakaoTalk' | 'SMS' | 'Link' | 'Other') 하므로 단순 캐스팅.
const toDeliveryShareChannel = (channel: ShareChannel): GiftDeliveryShareChannel =>
  channel as unknown as GiftDeliveryShareChannel;

interface UseGiftOrderShareParams {
  orderId: string;
  order: GiftOrderForStore | null;
  role: GiftStoreRole;
  courtesyContent: string;
}

export function useGiftOrderShare({
  orderId,
  order,
  role,
  courtesyContent,
}: UseGiftOrderShareParams) {
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId));
  const queryClient = useQueryClient();

  const syncCourtesyIfNeeded = useCallback(async () => {
    if (!orderId || !courtesyContent || order?.courtesyContent === courtesyContent)
      return;
    try {
      const updated = await updateGiftOrderCourtesy(orderId, { courtesyContent }, role);
      setOrderData((prev) =>
        prev ? { ...prev, courtesyContent: updated.courtesyContent } : prev,
      );
    } catch (error) {
      console.error('답례글 자동 저장 실패:', error);
    }
  }, [orderId, courtesyContent, order?.courtesyContent, role, setOrderData]);

  const invalidateOrder = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey === 'string' &&
        query.queryKey.includes(`/gift-order/${orderId}`),
    });
  }, [queryClient, orderId]);

  // === 파생값/통계 (쿠폰·배송 목록, 배송모드 플래그, 전달 통계) — 순수 훅으로 분리 ===
  const {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,
    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  } = useGiftOrderShareItems(order);

  // === 메모 모달 / 번호잠금 수정 모달 — 독립 모달 훅으로 분리 ===
  const {
    showMemoModal,
    isMemoSaving,
    memoExistingValue,
    handleOpenMemoCoupon,
    handleOpenMemoDelivery,
    handleMemoSave,
    handleMemoClose,
  } = useGiftMemoModal({ role, invalidateOrder });

  const {
    showPhoneLockEdit,
    isPhoneLockSaving,
    phoneLockTarget,
    handleOpenPhoneLockEdit,
    handleClosePhoneLockEdit,
    handlePhoneLockSave,
  } = useGiftPhoneLockEditModal({ role, setOrderData });

  // === 공유 가이드 모달 ===
  const [showShareGuide, setShowShareGuide] = useState(false);
  const pendingShareRef = useRef<{
    type: 'coupon' | 'delivery';
    item: GiftCouponForStore | GiftDeliveryForStore;
  } | null>(null);

  // === 쿠폰 / 배송 공유 모달 (하단 시트) ===
  // 두 모달의 open 상태 setter 는 카카오 웹훅 onSuccess 안에서도 참조하므로
  // (= 카톡 다녀와서 ShareConfirmModal 띄울 때 하단 시트를 닫기 위함)
  // 훅 호출을 kakaoShareCheck 보다 위로 끌어올려 둔다.
  const couponShareModalOpenId = `gift-coupon-share-${orderId}`;
  const setCouponShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(couponShareModalOpenId),
  );
  const deliveryShareModalOpenId = `gift-delivery-share-${orderId}`;
  const setDeliveryShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(deliveryShareModalOpenId),
  );
  const [currentShareCoupon, setCurrentShareCoupon] = useState<GiftCouponForStore | null>(
    null,
  );
  const [couponShareOpenCount, setCouponShareOpenCount] = useState(0);

  const currentCouponShareUrl = useMemo(() => {
    if (!currentShareCoupon) return '';
    return `${window.location.origin}/gift-store/coupon/${currentShareCoupon.code}`;
  }, [currentShareCoupon]);

  const handleOpenCouponShareModal = useCallback(
    (coupon: GiftCouponForStore) => {
      setCurrentShareCoupon(coupon);
      setCouponShareOpenCount((c) => c + 1);
      setCouponShareModalOpen(true);
    },
    [setCouponShareModalOpen],
  );

  // === 전달 확인 모달 상태 ===
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [pendingConfirmItem, setPendingConfirmItem] = useState<PendingConfirmItem | null>(
    null,
  );
  const [isConfirmSaving, setIsConfirmSaving] = useState(false);
  const [lastShareChannel, setLastShareChannel] = useState<ShareChannel>(
    ShareChannel.KakaoTalk,
  );
  const [confirmSeq, setConfirmSeq] = useState<number | null>(null);

  // === 다건수신 의심 확인 모달 상태 (SuspicionConfirmModal) ===
  // 핸들러/복원 useEffect 가 모두 참조하므로 다른 useState 와 함께 상단에 배치.
  const [pendingSuspicion, setPendingSuspicion] =
    useState<PendingDuplicateSuspicionEntry | null>(null);
  const [showSuspicionModal, setShowSuspicionModal] = useState(false);
  const [isSuspicionSaving, setIsSuspicionSaving] = useState(false);
  /**
   * "잘못 전달 의심" 으로 표시할 itemId 집합. localStorage 의 의심 마크를
   * 미러링 하며 save/remove 헬퍼와 함께 갱신한다 (UI 가 reactive 하게 별도
   * "의심 목록" 섹션을 그릴 수 있도록).
   *
   * 새로고침 직후 첫 렌더부터 의심 섹션에 분류되도록 useState lazy initializer
   * 로 localStorage 에서 즉시 로드한다 (order/orderId 가 아직 없어도 OK —
   * 전체 의심 마크에서 itemId 만 모아두고, 분류는 itemId 기준으로 동작).
   */
  const [suspectedItemIds, setSuspectedItemIds] = useState<Set<string>>(() => {
    try {
      const all = loadPendingDuplicateSuspicions();
      return new Set(all.map((e) => e.itemId));
    } catch {
      return new Set();
    }
  });

  const addSuspectedItemId = useCallback((itemId: string) => {
    setSuspectedItemIds((prev) => {
      if (prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  }, []);

  const removeSuspectedItemId = useCallback((itemId: string) => {
    setSuspectedItemIds((prev) => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  // === 카카오 공유 웹훅 확인 ===
  // 카카오톡 공유 시 백엔드가 웹훅 수신과 동시에 shareStatus(NotShared → Shared) +
  // kakaoSharedAt 을 자동 세팅하므로, 프론트는 가벼운 public 단건 조회 API 로
  // 그 두 필드를 폴링해서 확인만 한다 (POST /share/:id 호출 없음).
  //
  // 판별 기준:
  //   shareStatus !== NotShared && kakaoSharedAt != null
  //
  // 재전송 엣지 케이스: unshare 시 shareStatus 가 NotShared 로 리셋되므로,
  // 다음 카카오 시도 때 실제로 안 보내면 첫 조건이 fail → 오탐 없음.
  const kakaoShareCheck = useKakaoShareWebhookCheck({
    fetchShareState: async (kind, entityId) => {
      if (kind === 'coupon') {
        const c = await getGiftCouponById(entityId);
        return {
          isShared: c.shareStatus !== GiftCouponShareStatus.NotShared,
          kakaoSharedAt: c.kakaoSharedAt ? new Date(c.kakaoSharedAt) : null,
          lastSharedKakaoHashChatId: c.lastSharedKakaoHashChatId ?? null,
          duplicateRecipientEntries: c.duplicateRecipientCoupons ?? [],
        };
      }
      const d = await getGiftDeliveryById(entityId);
      return {
        isShared: d.shareStatus !== GiftDeliveryShareStatus.NotShared,
        kakaoSharedAt: d.kakaoSharedAt ? new Date(d.kakaoSharedAt) : null,
        lastSharedKakaoHashChatId: d.lastSharedKakaoHashChatId ?? null,
        duplicateRecipientEntries: d.duplicateRecipientDeliveries ?? [],
      };
    },
    onSuccess: async (kind, entityId, snapshot) => {
      try {
        const detectedAt = snapshot.kakaoSharedAt ?? new Date();
        // 다건수신(같은 분께 또 보냄) 판정용 필드. 백엔드가 webhook 처리 중에 갱신한
        // 최신 값이 fresh 하므로 snapshot 에서 받은 값을 그대로 쿠폰/배송에 머지한다.
        const lastHash = snapshot.lastSharedKakaoHashChatId ?? undefined;

        if (kind === 'coupon') {
          const dupEntries =
            snapshot.duplicateRecipientEntries as DuplicateRecipientCouponEntry[];
          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === entityId
                  ? {
                      ...c,
                      shareStatus:
                        c.shareStatus === GiftCouponShareStatus.NotShared
                          ? GiftCouponShareStatus.Shared
                          : c.shareStatus,
                      sharedAt: c.sharedAt ?? detectedAt,
                      kakaoSharedAt: detectedAt,
                      // 백엔드 웹훅이 lastSharedChannel 을 KakaoTalk 으로 덮어쓰므로 미러링.
                      lastSharedChannel: ShareChannel.KakaoTalk,
                      lastSharedKakaoHashChatId: lastHash,
                      duplicateRecipientCoupons: dupEntries,
                    }
                  : c,
              ),
            };
          });
          const coupon = couponList.find((c) => c._id === entityId);
          if (coupon) {
            const seq = couponList.findIndex((c) => c._id === entityId) + 1;
            savePendingConfirm({
              type: 'coupon',
              itemId: entityId,
              seq,
              channel: ShareChannel.KakaoTalk,
              orderId,
              timestamp: Date.now(),
            });
            // 카톡 다녀와서 ShareConfirmModal 띄우기 전에 하단 공유 시트는 닫는다.
            setCouponShareModalOpen(false);
            setLastShareChannel(ShareChannel.KakaoTalk);
            setConfirmSeq(seq);
            setPendingConfirmItem({
              type: 'coupon',
              item: {
                ...coupon,
                kakaoSharedAt: detectedAt,
                lastSharedChannel: ShareChannel.KakaoTalk,
                lastSharedKakaoHashChatId: lastHash,
                duplicateRecipientCoupons: dupEntries,
              },
            });
            setShowShareConfirm(true);
          }
        } else {
          const dupEntries =
            snapshot.duplicateRecipientEntries as DuplicateRecipientDeliveryEntry[];
          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === entityId
                  ? {
                      ...d,
                      shareStatus:
                        d.shareStatus === GiftDeliveryShareStatus.NotShared
                          ? GiftDeliveryShareStatus.Shared
                          : d.shareStatus,
                      sharedAt: d.sharedAt ?? detectedAt,
                      kakaoSharedAt: detectedAt,
                      lastSharedChannel: GiftDeliveryShareChannel.KakaoTalk,
                      lastSharedKakaoHashChatId: lastHash,
                      duplicateRecipientDeliveries: dupEntries,
                    }
                  : d,
              ),
            };
          });
          const delivery = deliveryList.find((d) => d._id === entityId);
          if (delivery) {
            const seq = deliveryList.findIndex((d) => d._id === entityId) + 1;
            savePendingConfirm({
              type: 'delivery',
              itemId: entityId,
              seq,
              channel: ShareChannel.KakaoTalk,
              orderId,
              timestamp: Date.now(),
            });
            // 카톡 다녀와서 ShareConfirmModal 띄우기 전에 하단 공유 시트는 닫는다.
            setDeliveryShareModalOpen(false);
            setLastShareChannel(ShareChannel.KakaoTalk);
            setConfirmSeq(seq);
            setPendingConfirmItem({
              type: 'delivery',
              item: {
                ...delivery,
                kakaoSharedAt: detectedAt,
                lastSharedChannel: GiftDeliveryShareChannel.KakaoTalk,
                lastSharedKakaoHashChatId: lastHash,
                duplicateRecipientDeliveries: dupEntries,
              },
            });
            setShowShareConfirm(true);
          }
        }
        await syncCourtesyIfNeeded();
      } catch (error) {
        console.error('카카오 웹훅 확인 후 공유 상태 업데이트 실패:', error);
      }
    },
    onTimeout: () => {
      // 낙관 처리로 이미 Shared + 확인 모달까지 띄웠으므로 타임아웃은 정상(웹훅 지연일 뿐).
      // 실제 전달 확인은 재진입 시 sharedAt 20분 룰(아래 useEffect)로 사후 검증한다.
    },
  });

  const handleCouponShareComplete = useCallback(
    async (channel: ShareChannel) => {
      if (!currentShareCoupon) return;

      const seq = couponList.findIndex((c) => c._id === currentShareCoupon._id) + 1;

      savePendingConfirm({
        type: 'coupon',
        itemId: currentShareCoupon._id,
        seq,
        channel,
        orderId,
        timestamp: Date.now(),
      });

      try {
        await Promise.all([
          shareCoupon(currentShareCoupon._id, role, channel),
          syncCourtesyIfNeeded(),
        ]);
        setOrderData((prev) => {
          if (!prev || !prev.coupons) return prev;
          return {
            ...prev,
            coupons: prev.coupons.map((c) =>
              c._id === currentShareCoupon._id
                ? {
                    ...c,
                    shareStatus: GiftCouponShareStatus.Shared,
                    // 백엔드가 share API 본문의 shareChannel 로 lastSharedChannel 을 갱신하므로 미러링.
                    lastSharedChannel: channel,
                  }
                : c,
            ),
          };
        });
      } catch (error) {
        console.error('공유 상태 업데이트 실패:', error);
      }

      setLastShareChannel(channel);
      setConfirmSeq(seq);
      setPendingConfirmItem({
        type: 'coupon',
        item: { ...currentShareCoupon, lastSharedChannel: channel },
      });
      // 공유 시트는 닫고 확인 모달만 남긴다 (SMS·카카오·다른 앱 공통).
      setCouponShareModalOpen(false);
      setShowShareConfirm(true);

      // 카카오는 위에서 이미 낙관적으로 Shared 처리했고(중복 발송 방지), 웹훅 폴링은
      // 실제 전달 시각(kakaoSharedAt)과 다건수신 정보를 사후에 덧입히는 용도로만 돌린다.
      // NotShared→Shared 승격은 멱등이라 웹훅이 늦게/안 와도 문제없다.
      if (channel === ShareChannel.KakaoTalk) {
        kakaoShareCheck.start('coupon', currentShareCoupon._id);
      }
    },
    [
      currentShareCoupon,
      couponList,
      orderId,
      role,
      setOrderData,
      syncCourtesyIfNeeded,
      kakaoShareCheck,
      setCouponShareModalOpen,
    ],
  );

  const handleCouponShare = useCallback(
    (coupon: GiftCouponForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenCouponShareModal(coupon);
      } else {
        pendingShareRef.current = { type: 'coupon', item: coupon };
        setShowShareGuide(true);
      }
    },
    [handleOpenCouponShareModal],
  );

  // === 배송 공유 모달 (Individual) ===
  // openId / setOpen 은 위쪽 "쿠폰 / 배송 공유 모달" 블록에서 한 번에 선언함.
  const [currentShareDelivery, setCurrentShareDelivery] =
    useState<GiftDeliveryForStore | null>(null);
  const [deliveryShareOpenCount, setDeliveryShareOpenCount] = useState(0);

  const currentDeliveryShareUrl = useMemo(() => {
    if (!currentShareDelivery) return '';
    return `${window.location.origin}/gift-store/delivery/${currentShareDelivery.code}`;
  }, [currentShareDelivery]);

  const handleOpenDeliveryShareModal = useCallback(
    (delivery: GiftDeliveryForStore) => {
      setCurrentShareDelivery(delivery);
      setDeliveryShareOpenCount((c) => c + 1);
      setDeliveryShareModalOpen(true);
    },
    [setDeliveryShareModalOpen],
  );

  const handleDeliveryShareComplete = useCallback(
    async (channel: ShareChannel) => {
      if (!currentShareDelivery) return;

      const seq = deliveryList.findIndex((d) => d._id === currentShareDelivery._id) + 1;

      savePendingConfirm({
        type: 'delivery',
        itemId: currentShareDelivery._id,
        seq,
        channel,
        orderId,
        timestamp: Date.now(),
      });

      const deliveryChannel = toDeliveryShareChannel(channel);

      try {
        await Promise.all([
          shareGiftDelivery(currentShareDelivery._id, role, deliveryChannel),
          syncCourtesyIfNeeded(),
        ]);
        setOrderData((prev) => {
          if (!prev || !prev.deliveries) return prev;
          return {
            ...prev,
            deliveries: prev.deliveries.map((d) =>
              d._id === currentShareDelivery._id
                ? {
                    ...d,
                    shareStatus: GiftDeliveryShareStatus.Shared,
                    lastSharedChannel: deliveryChannel,
                  }
                : d,
            ),
          };
        });
      } catch (error) {
        console.error('배송 공유 상태 업데이트 실패:', error);
      }

      setLastShareChannel(channel);
      setConfirmSeq(seq);
      setPendingConfirmItem({
        type: 'delivery',
        item: { ...currentShareDelivery, lastSharedChannel: deliveryChannel },
      });
      // 공유 시트는 닫고 확인 모달만 남긴다 (SMS·카카오·다른 앱 공통).
      setDeliveryShareModalOpen(false);
      setShowShareConfirm(true);

      // 카카오는 위에서 이미 낙관적으로 Shared 처리했고, 웹훅 폴링은 kakaoSharedAt /
      // 다건수신 정보를 사후에 덧입히는 용도로만 돌린다. (쿠폰 경로와 동일)
      if (channel === ShareChannel.KakaoTalk) {
        kakaoShareCheck.start('delivery', currentShareDelivery._id);
      }
    },
    [
      currentShareDelivery,
      deliveryList,
      orderId,
      role,
      setOrderData,
      syncCourtesyIfNeeded,
      kakaoShareCheck,
      setDeliveryShareModalOpen,
    ],
  );

  const handleDeliveryShare = useCallback(
    (delivery: GiftDeliveryForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenDeliveryShareModal(delivery);
      } else {
        pendingShareRef.current = { type: 'delivery', item: delivery };
        setShowShareGuide(true);
      }
    },
    [handleOpenDeliveryShareModal],
  );

  // 전달 시 순번 (전체 쿠폰 목록에서의 index)
  const currentShareSeq = useMemo(() => {
    if (!currentShareCoupon) return null;
    const idx = couponList.findIndex((c) => c._id === currentShareCoupon._id);
    return idx >= 0 ? idx + 1 : null;
  }, [currentShareCoupon, couponList]);

  const currentDeliverySeq = useMemo(() => {
    if (!currentShareDelivery) return null;
    const idx = deliveryList.findIndex((d) => d._id === currentShareDelivery._id);
    return idx >= 0 ? idx + 1 : null;
  }, [currentShareDelivery, deliveryList]);

  // 페이지 진입 시 localStorage에서 미확인 전달 건 복원.
  // 다건수신 의심 마크가 있으면 우선 SuspicionConfirmModal 을 띄우고,
  // 그 외에는 일반 ShareConfirmModal 을 띄운다 (둘이 동시에 뜨지 않게 우선순위).
  // 의심 마크가 있다면 같이 suspectedItemIds 에도 hydrate 해서 "의심 목록"
  // 섹션이 reactive 하게 그려지도록 한다.
  useEffect(() => {
    if (!orderId || !order) return;

    // === 카카오 낙관 전달 후 webhook 미확인분 미전달 복구 ===
    // 카카오는 전달 즉시 낙관적으로 Shared 처리하므로(중복 발송 방지), 공유창만 열고
    // 취소한 경우에도 Shared 로 남아 영영 전달되지 않을 수 있다. 그래서 재진입 시
    // "카카오로 보냈다고 표시됐지만 실제 전달(kakaoSharedAt)이 sharedAt 으로부터 20분이
    // 지나도록 확인되지 않은" 건은 미전달로 되살린다.
    //   - 웹훅 흔적(kakaoSharedAt·shareKakaoCount)은 백엔드가 웹훅 수신 시 영구 저장 →
    //     재진입 시 새로 조회된 order 에 도착 여부가 반영돼 있으므로 이 판정의 근거가 된다.
    //   - 실제 전달인데 웹훅이 20분 넘게 늦는 경우는 없다고 가정. (있으면 재전송 시 중복
    //     발송 위험이 있으나, 누락 방지가 더 중요하다는 정책적 선택.)
    //   - confirmedAt(운영자 "네 보냈어요")은 일부러 보지 않는다 — 실제로 안 보냈는데도
    //     반사적으로 확인을 누르는 경우가 많아 실제 전달의 증거가 못 되기 때문.
    //   - 대신 shareStatus===Shared(열람 전) + redeemStatus/deliveryStatus 미진행 조건으로
    //     "수신자가 열람/교환한" 진짜 전달 흔적이 있으면 건드리지 않도록 안전망을 둔다.
    const KAKAO_CONFIRM_GRACE_MS = 20 * 60 * 1000;
    const now = Date.now();
    const isUnconfirmedKakao = (item: {
      lastSharedChannel?: string;
      kakaoSharedAt?: Date;
      shareKakaoCount?: number;
      sharedAt?: Date;
    }) =>
      item.lastSharedChannel === ShareChannel.KakaoTalk &&
      !item.kakaoSharedAt &&
      (item.shareKakaoCount ?? 0) === 0 &&
      !!item.sharedAt &&
      now - new Date(item.sharedAt).getTime() >= KAKAO_CONFIRM_GRACE_MS;

    const staleCoupons = couponList.filter(
      (c) =>
        c.shareStatus === GiftCouponShareStatus.Shared &&
        c.redeemStatus === GiftCouponRedeemStatus.Available &&
        isUnconfirmedKakao(c),
    );
    const staleDeliveries = deliveryList.filter(
      (d) =>
        d.shareStatus === GiftDeliveryShareStatus.Shared &&
        d.deliveryStatus === GiftDeliveryStatus.WaitingAddress &&
        isUnconfirmedKakao(d),
    );

    if (staleCoupons.length > 0 || staleDeliveries.length > 0) {
      // pendingConfirm 마크를 먼저 동기 제거 → 아래 복원 로직이 이 건들의 확인 모달을
      // 다시 띄우지 않도록 (이 effect 안에서 순차 실행되므로 순서 보장).
      [...staleCoupons, ...staleDeliveries].forEach((item) =>
        removePendingConfirm(item._id),
      );
      // 로컬 상태 즉시 NotShared 로 (미전달 섹션으로 이동)
      const staleCouponIds = new Set(staleCoupons.map((c) => c._id));
      const staleDeliveryIds = new Set(staleDeliveries.map((d) => d._id));
      setOrderData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          coupons: prev.coupons?.map((c) =>
            staleCouponIds.has(c._id)
              ? { ...c, shareStatus: GiftCouponShareStatus.NotShared }
              : c,
          ),
          deliveries: prev.deliveries?.map((d) =>
            staleDeliveryIds.has(d._id)
              ? { ...d, shareStatus: GiftDeliveryShareStatus.NotShared }
              : d,
          ),
        };
      });
      // 백엔드에도 반영 (unshare). allSettled 라 일부 실패해도 다음 재진입에서 재시도됨.
      void Promise.allSettled([
        ...staleCoupons.map((c) => unshareCoupon(c._id, role)),
        ...staleDeliveries.map((d) => unshareGiftDelivery(d._id, role)),
      ]);
    }

    // 같은 주문의 모든 의심 마크를 suspectedItemIds 에 hydrate.
    // (확인 모달은 첫 entry 한 건만 자동 노출 — 나머지는 사용자가 의심 섹션에서
    //  "결과 알려주기" 로 개별 처리)
    //
    // ⚠️ 주의: stillRelevant 체크로 자동 삭제하지 않는다. 마크는 사용자가 의심 모달에서
    // 명시적으로 "잘 보냄" / "중복 삭제" / "다시 보내기" 를 선택할 때만 정리한다.
    // 자동 삭제하면 hydrate 타이밍이 어긋나거나 백엔드 동기화 지연 시 마크가
    // 영구 소실되어 "새로고침하니 전달완료로 가있다" 같은 버그가 생긴다.
    const suspicions = loadPendingDuplicateSuspicionsForOrder(orderId);
    if (suspicions.length > 0) {
      suspicions.forEach((s) => addSuspectedItemId(s.itemId));
      setPendingSuspicion(suspicions[0] ?? null);
      setShowSuspicionModal(true);
      return;
    }

    const pending = loadPendingConfirmForOrder(orderId);
    if (!pending) return;

    if (pending.type === 'coupon') {
      const coupon = couponList.find((c) => c._id === pending.itemId);
      if (coupon && !coupon.confirmedAt) {
        setPendingConfirmItem({ type: 'coupon', item: coupon });
        setLastShareChannel(pending.channel);
        setConfirmSeq(pending.seq);
        setShowShareConfirm(true);
      } else {
        removePendingConfirm(pending.itemId);
      }
    } else {
      const delivery = deliveryList.find((d) => d._id === pending.itemId);
      if (delivery && !delivery.confirmedAt) {
        setPendingConfirmItem({ type: 'delivery', item: delivery });
        setLastShareChannel(pending.channel);
        setConfirmSeq(pending.seq);
        setShowShareConfirm(true);
      } else {
        removePendingConfirm(pending.itemId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, order?._id]);

  // "네, 보냈어요" → confirm API 호출 (memo optional) + 데이터 새로고침
  const handleShareConfirm = useCallback(
    async (memo: string) => {
      if (!pendingConfirmItem) return;
      setIsConfirmSaving(true);

      try {
        const trimmedMemo = memo.trim() || undefined;

        if (pendingConfirmItem.type === 'coupon') {
          await confirmCoupon(pendingConfirmItem.item._id, role, trimmedMemo);
        } else {
          await confirmDelivery(pendingConfirmItem.item._id, role, trimmedMemo);
        }
        removePendingConfirm(pendingConfirmItem.item._id);
        invalidateOrder();
      } catch (error) {
        console.error('전달 확인 실패:', error);
      } finally {
        setIsConfirmSaving(false);
        setShowShareConfirm(false);
        setPendingConfirmItem(null);
        setConfirmSeq(null);
      }
    },
    [pendingConfirmItem, role, invalidateOrder],
  );

  const performUnshare = useCallback(async () => {
    if (!pendingConfirmItem) return null;
    setIsConfirmSaving(true);
    const item = pendingConfirmItem;
    try {
      if (item.type === 'coupon') {
        await unshareCoupon(item.item._id, role);
        setOrderData((prev) => {
          if (!prev || !prev.coupons) return prev;
          return {
            ...prev,
            coupons: prev.coupons.map((c) =>
              c._id === item.item._id
                ? { ...c, shareStatus: GiftCouponShareStatus.NotShared }
                : c,
            ),
          };
        });
      } else {
        await unshareGiftDelivery(item.item._id, role);
        setOrderData((prev) => {
          if (!prev || !prev.deliveries) return prev;
          return {
            ...prev,
            deliveries: prev.deliveries.map((d) =>
              d._id === item.item._id
                ? { ...d, shareStatus: GiftDeliveryShareStatus.NotShared }
                : d,
            ),
          };
        });
      }
      removePendingConfirm(item.item._id);
    } catch (error) {
      console.error('공유 취소 실패:', error);
    } finally {
      setIsConfirmSaving(false);
      setShowShareConfirm(false);
      setPendingConfirmItem(null);
      setConfirmSeq(null);
    }
    return item;
  }, [pendingConfirmItem, role, setOrderData]);

  // "다른 방법으로 보내기" → unshare + 해당 아이템 공유 모달 바로 다시 열기
  const handleShareConfirmDeny = useCallback(async () => {
    const item = await performUnshare();
    if (!item) return;
    if (item.type === 'coupon') {
      handleOpenCouponShareModal(item.item as GiftCouponForStore);
    } else {
      handleOpenDeliveryShareModal(item.item as GiftDeliveryForStore);
    }
  }, [performUnshare, handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // "나중에 할게요" → unshare만 하고 모달 닫기
  const handleShareConfirmLater = useCallback(async () => {
    await performUnshare();
  }, [performUnshare]);

  const handleShareConfirmClose = useCallback(() => {
    // localStorage에서 제거하지 않음 → 다음 페이지 진입 시 다시 표시됨
    setShowShareConfirm(false);
    setPendingConfirmItem(null);
    setConfirmSeq(null);
  }, []);

  // ============================================================
  // SuspicionConfirmModal 핸들러
  // ============================================================
  // 의심 마크가 가리키는 답례품을 현재 주문 상태에서 다시 찾아서 처리한다.
  // (페이지 reload 사이에 항목이 바뀌었을 가능성 대비)
  const findSuspicionItem = useCallback(():
    | { type: 'coupon'; item: GiftCouponForStore }
    | { type: 'delivery'; item: GiftDeliveryForStore }
    | null => {
    if (!pendingSuspicion) return null;
    if (pendingSuspicion.type === 'coupon') {
      const coupon = couponList.find((c) => c._id === pendingSuspicion.itemId);
      return coupon ? { type: 'coupon', item: coupon } : null;
    }
    const delivery = deliveryList.find((d) => d._id === pendingSuspicion.itemId);
    return delivery ? { type: 'delivery', item: delivery } : null;
  }, [pendingSuspicion, couponList, deliveryList]);

  /**
   * "그분께 잘 보낸 거였어요" → 일반 confirm + 의심 마크 제거.
   * 메모 없이 바로 confirmedAt 만 찍는다 (사용자가 더 이상 입력할 게 없는 흐름).
   */
  const handleSuspicionConfirmedSafe = useCallback(async () => {
    if (!pendingSuspicion) return;
    const target = findSuspicionItem();
    setIsSuspicionSaving(true);
    try {
      if (target) {
        if (target.type === 'coupon') {
          await confirmCoupon(target.item._id, role, undefined);
        } else {
          await confirmDelivery(target.item._id, role, undefined);
        }
      }
      removePendingDuplicateSuspicion(pendingSuspicion.itemId);
      removeSuspectedItemId(pendingSuspicion.itemId);
      // pendingConfirm 도 같이 정리 (중복 노출 방지)
      removePendingConfirm(pendingSuspicion.itemId);
      invalidateOrder();
    } catch (error) {
      console.error('의심 확인(잘 보냄) 처리 실패:', error);
    } finally {
      setIsSuspicionSaving(false);
      setShowSuspicionModal(false);
      setPendingSuspicion(null);
    }
  }, [pendingSuspicion, findSuspicionItem, role, invalidateOrder, removeSuspectedItemId]);

  /**
   * "중복이라 삭제했어요" 처리.
   * 즉시 unshare 해서 NotShared 로 되돌리고, 의심/pendingConfirm 마크를 모두 정리한다.
   * 모달 자체는 닫지 않는다 — 호출부(SuspicionConfirmModal)가 후속으로 "다시 보낼래요?"
   * step 으로 전환하기 위해서.
   *
   * pendingSuspicion 은 의도적으로 보존한다 (`handleSuspicionResend` 가 같은 itemId
   * 로 공유 시트를 열어야 하기 때문). 모달이 완전히 닫힐 때 (`handleSuspicionClose`)
   * 일괄 nullify 한다.
   *
   * @returns true: 후속 step 진입 가능 / false: 실패 (모달 ask 단계 유지)
   */
  const handleSuspicionDelete = useCallback(async (): Promise<boolean> => {
    if (!pendingSuspicion) return false;
    const target = findSuspicionItem();
    setIsSuspicionSaving(true);
    try {
      if (target) {
        if (target.type === 'coupon') {
          await unshareCoupon(target.item._id, role);
          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === target.item._id
                  ? { ...c, shareStatus: GiftCouponShareStatus.NotShared }
                  : c,
              ),
            };
          });
        } else {
          await unshareGiftDelivery(target.item._id, role);
          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === target.item._id
                  ? { ...d, shareStatus: GiftDeliveryShareStatus.NotShared }
                  : d,
              ),
            };
          });
        }
      }
      removePendingDuplicateSuspicion(pendingSuspicion.itemId);
      removeSuspectedItemId(pendingSuspicion.itemId);
      removePendingConfirm(pendingSuspicion.itemId);
      return true;
    } catch (error) {
      console.error('의심 확인(중복 삭제) 처리 실패:', error);
      return false;
    } finally {
      setIsSuspicionSaving(false);
    }
  }, [pendingSuspicion, findSuspicionItem, role, setOrderData, removeSuspectedItemId]);

  /**
   * "지금 다시 보내기" 처리.
   * `handleSuspicionDelete` 가 이미 unshare 까지 끝낸 상태이므로 (item 은 NotShared),
   * 단순히 같은 itemId 의 공유 시트를 다시 연다. 사용자는 카톡/문자/다른앱 중 자유롭게
   * 채널을 선택해 재전달하며, 기존 첫 공유와 동일한 flow (kakao webhook 폴링 →
   * 새 ShareConfirmModal 자동 노출) 가 그대로 동작한다.
   */
  const handleSuspicionResend = useCallback(() => {
    if (!pendingSuspicion) return;
    const target = findSuspicionItem();
    setShowSuspicionModal(false);
    setPendingSuspicion(null);
    if (!target) return;
    if (target.type === 'coupon') {
      handleOpenCouponShareModal(target.item);
    } else {
      handleOpenDeliveryShareModal(target.item);
    }
  }, [
    pendingSuspicion,
    findSuspicionItem,
    handleOpenCouponShareModal,
    handleOpenDeliveryShareModal,
  ]);

  /**
   * 의심 모달의 닫기 (X / "닫기" / 백드롭).
   *
   * - ask 단계: localStorage 마크 보존 → 다음 진입 시 재노출
   * - resendAsk 단계 (이미 handleSuspicionDelete 거쳐 unshare 완료): 단순 닫기.
   *   item 은 이미 미전달 섹션에 있으므로 추가 처리 불필요.
   */
  const handleSuspicionClose = useCallback(() => {
    if (isSuspicionSaving) return;
    setShowSuspicionModal(false);
    setPendingSuspicion(null);
  }, [isSuspicionSaving]);

  /**
   * "잘못 전달 의심" 섹션에서 행 탭 → SuspicionConfirmModal 재호출.
   * localStorage 마크 → state 로 다시 끌어와서 모달을 띄운다.
   */
  const reopenSuspicionModal = useCallback((itemId: string) => {
    const all = loadPendingDuplicateSuspicions();
    const found = all.find((e) => e.itemId === itemId);
    if (!found) return;
    setPendingSuspicion(found);
    setShowSuspicionModal(true);
  }, []);

  /**
   * 사용자가 카톡방을 확인하고 답례품 페이지로 복귀했을 때 SuspicionConfirmModal
   * 을 자동으로 다시 띄운다. (ShareConfirmModal 이 카톡 webhook 폴링 후 자동
   * 노출되는 것과 동일한 UX 의도 — "돌아왔으니 바로 결과 입력하게끔")
   *
   * 트리거:
   *   - document visibilitychange → 'visible'
   *   - window focus
   *
   * 가드:
   *   - 이 주문의 pending suspicion 마크가 localStorage 에 남아있어야 함
   *   - 현재 처리 중(isSuspicionSaving) 이거나 이미 모달이 떠있으면 무시
   */
  useEffect(() => {
    if (!orderId) return;
    const tryReopen = () => {
      // 1) suspectedItemIds 를 localStorage 와 항상 동기화 (의심 섹션 분류 보존)
      const all = loadPendingDuplicateSuspicionsForOrder(orderId);
      if (all.length > 0) {
        setSuspectedItemIds((prev) => {
          let changed = false;
          const next = new Set(prev);
          for (const s of all) {
            if (!next.has(s.itemId)) {
              next.add(s.itemId);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
      // 2) 모달 자동 노출 (이미 떠있거나 처리 중이면 스킵)
      if (isSuspicionSaving) return;
      if (showSuspicionModal) return;
      const found = loadPendingDuplicateSuspicionForOrder(orderId);
      if (!found) return;
      setPendingSuspicion(found);
      setShowSuspicionModal(true);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tryReopen();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', tryReopen);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', tryReopen);
    };
  }, [orderId, isSuspicionSaving, showSuspicionModal]);

  /**
   * 카카오톡으로 잘못 보낸 케이스의 회수 플로우 (쿠폰 / 배송 공통).
   *
   * 흐름:
   *   1) `PATCH /:role/gift-coupon|gift-delivery/restricted-phone/:id` 로 수령자 번호 잠금
   *      → 백엔드는 `additionalRecipientPhone` 만 갱신, redeem 시 이 번호로만 본인 인증 허용
   *   2) 기존 공유 상태를 NotShared 로 리셋 (먼저 받은 분이 메시지를 그대로 가지고 있어도
   *      번호 잠금이 걸려 있어 수령이 불가)
   *   3) 확인 모달을 닫고, 곧바로 같은 답례품의 공유 시트를 다시 열어서 정정 발송 유도
   */
  const handleShareConfirmRecover = useCallback(
    async (phoneNumber: string, memo?: string) => {
      if (!pendingConfirmItem) return;
      setIsConfirmSaving(true);
      const trimmedMemo = memo?.trim() || undefined;
      try {
        if (pendingConfirmItem.type === 'coupon') {
          const coupon = pendingConfirmItem.item;
          await updateCouponRestrictedPhone(coupon._id, phoneNumber, role, trimmedMemo);
          await unshareCoupon(coupon._id, role);

          const updatedCoupon: GiftCouponForStore = {
            ...coupon,
            shareStatus: GiftCouponShareStatus.NotShared,
            kakaoSharedAt: undefined,
            additionalRecipientPhone: phoneNumber,
            memo: trimmedMemo ?? coupon.memo,
          };

          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === coupon._id ? updatedCoupon : c,
              ),
            };
          });
          removePendingConfirm(coupon._id);

          setShowShareConfirm(false);
          setPendingConfirmItem(null);
          setConfirmSeq(null);
          handleOpenCouponShareModal(updatedCoupon);
        } else {
          const delivery = pendingConfirmItem.item;
          await updateDeliveryRestrictedPhone(
            delivery._id,
            phoneNumber,
            role,
            trimmedMemo,
          );
          await unshareGiftDelivery(delivery._id, role);

          const updatedDelivery: GiftDeliveryForStore = {
            ...delivery,
            shareStatus: GiftDeliveryShareStatus.NotShared,
            kakaoSharedAt: undefined,
            additionalRecipientPhone: phoneNumber,
            memo: trimmedMemo ?? delivery.memo,
          };

          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === delivery._id ? updatedDelivery : d,
              ),
            };
          });
          removePendingConfirm(delivery._id);

          setShowShareConfirm(false);
          setPendingConfirmItem(null);
          setConfirmSeq(null);
          handleOpenDeliveryShareModal(updatedDelivery);
        }
      } catch (error) {
        console.error('회수(번호 잠금) 실패:', error);
        toastError('번호 저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
      } finally {
        setIsConfirmSaving(false);
      }
    },
    [
      pendingConfirmItem,
      role,
      setOrderData,
      handleOpenCouponShareModal,
      handleOpenDeliveryShareModal,
    ],
  );

  // === 하단 버튼 (쿠폰 + 배송 통합) ===
  const handleBottomButtonClick = useCallback(() => {
    const firstUnsharedCoupon = couponList.find(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    );
    if (firstUnsharedCoupon) {
      handleCouponShare(firstUnsharedCoupon);
      return;
    }

    if (isIndividualDelivery) {
      const firstUnsharedDelivery = deliveryList.find(
        (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
      );
      if (firstUnsharedDelivery) {
        handleDeliveryShare(firstUnsharedDelivery);
      }
    }
  }, [
    couponList,
    deliveryList,
    isIndividualDelivery,
    handleCouponShare,
    handleDeliveryShare,
  ]);

  const handleShareGuideConfirm = useCallback(() => {
    setShowShareGuide(false);
    if (pendingShareRef.current) {
      if (pendingShareRef.current.type === 'coupon') {
        handleOpenCouponShareModal(pendingShareRef.current.item as GiftCouponForStore);
      } else {
        handleOpenDeliveryShareModal(
          pendingShareRef.current.item as GiftDeliveryForStore,
        );
      }
      pendingShareRef.current = null;
    }
  }, [handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // ShareConfirmModal 의 톤(확정형 vs 추정형)을 결정하기 위해 노출.
  // 카톡 웹훅이 도착해 백엔드가 실제 전달을 확인했으면 값이 들어있다.
  const confirmKakaoSharedAt: Date | null = pendingConfirmItem?.item.kakaoSharedAt
    ? new Date(pendingConfirmItem.item.kakaoSharedAt)
    : null;
  const confirmItemKind: 'coupon' | 'delivery' | null = pendingConfirmItem
    ? pendingConfirmItem.type
    : null;
  // 마지막 공유 채널(백엔드 기록). ShareChannel / GiftDeliveryShareChannel 모두
  // 'KakaoTalk' 문자열 값을 공유하므로 모달에서는 단순 문자열 비교로 사용한다.
  const confirmLastSharedChannel: string | null =
    pendingConfirmItem?.item.lastSharedChannel ?? null;
  // 재전달 케이스에서 confirm 모달 메모 입력에 prefill 하기 위해 노출.
  const confirmExistingMemo: string | undefined = pendingConfirmItem?.item.memo;
  // 회수 플로우 또는 예방 잠금으로 잠금된 번호. 모달에서 부가 정보로 표시.
  const confirmAdditionalRecipientPhone: string | undefined =
    pendingConfirmItem?.item.additionalRecipientPhone;

  // 다건수신 충돌. 카톡 webhook 도착 후 같은 카톡방으로 공유된 다른 답례품이 있는 경우
  // ShareConfirmModal 이 메모 입력 대신 "같은 분께 또 보낸 것 같아요" 안내로 분기한다.
  // (lastSharedKakaoHashChatId === entry.hashChatId 조건으로 한 번 더 필터링됨)
  const confirmDuplicateConflicts: DuplicateRecipientConflict[] = useMemo(() => {
    if (!pendingConfirmItem) return [];
    if (pendingConfirmItem.type === 'coupon') {
      return getDuplicateRecipientConflictsForCoupon(pendingConfirmItem.item);
    }
    return getDuplicateRecipientConflictsForDelivery(pendingConfirmItem.item);
  }, [pendingConfirmItem]);

  // ============================================================
  // ShareConfirmModal "확인하러 가기" → SuspicionConfirmModal 진입 핸들러
  // ============================================================
  // 흐름 (SuspicionConfirmModal 핸들러는 위쪽 'SuspicionConfirmModal 핸들러' 블록 참고):
  //   1) ShareConfirmModal 다건수신 안내에서 "확인하러 가기" 클릭
  //      → handleShareConfirmInvestigate 가 localStorage 에 의심 마크 저장 + 모달 닫기
  //   2) 사용자가 카톡방 확인 후 페이지 복귀 시 useEffect 가 마크 복원해서
  //      SuspicionConfirmModal 자동 노출
  //   3) 모달 결과:
  //      - 잘 보낸 거였어요 → confirm + 의심 마크 제거
  //      - 중복이라 삭제했어요 → "지금 다시 보내시겠어요?" step
  //          - 예: 의심 마크 제거 + 재공유 (unshare X, 그냥 공유 시트 재오픈)
  //          - 아니오: 의심 마크 제거 + unshare → NotShared 복귀
  const handleShareConfirmInvestigate = useCallback(() => {
    if (!pendingConfirmItem) return;
    if (confirmDuplicateConflicts.length === 0) return;
    const item = pendingConfirmItem;
    const seq = confirmSeq ?? item.item.seq ?? 0;
    const lastHash = item.item.lastSharedKakaoHashChatId;
    if (!lastHash) return;
    savePendingDuplicateSuspicion({
      type: item.type,
      itemId: item.item._id,
      seq,
      orderId,
      hashChatId: lastHash,
      conflicts: confirmDuplicateConflicts.map((c) => ({ seq: c.seq, code: c.code })),
      timestamp: Date.now(),
    });
    addSuspectedItemId(item.item._id);
    // ShareConfirmModal 만 닫고 pendingConfirm(전달완료 메모 절차) 은 보존하지 않는다.
    // 의심 확인 모달에서 자체적으로 confirm/재공유/unshare 분기를 모두 수행하기 때문.
    setShowShareConfirm(false);
    setPendingConfirmItem(null);
    setConfirmSeq(null);
  }, [
    pendingConfirmItem,
    confirmDuplicateConflicts,
    confirmSeq,
    orderId,
    addSuspectedItemId,
  ]);

  return {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,

    couponShareModalOpenId,
    couponShareOpenCount,
    currentCouponShareUrl,
    currentShareSeq,
    currentShareCoupon,
    handleCouponShare,
    handleCouponShareComplete,

    deliveryShareModalOpenId,
    deliveryShareOpenCount,
    currentDeliveryShareUrl,
    currentDeliverySeq,
    currentShareDelivery,
    handleDeliveryShare,
    handleDeliveryShareComplete,

    showShareGuide,
    setShowShareGuide,
    handleBottomButtonClick,
    handleShareGuideConfirm,

    showShareConfirm,
    isConfirmSaving,
    lastShareChannel,
    confirmSeq,
    confirmKakaoSharedAt,
    confirmItemKind,
    confirmLastSharedChannel,
    confirmExistingMemo,
    confirmAdditionalRecipientPhone,
    confirmDuplicateConflicts,
    handleShareConfirm,
    handleShareConfirmDeny,
    handleShareConfirmLater,
    handleShareConfirmClose,
    handleShareConfirmRecover,
    handleShareConfirmInvestigate,

    // SuspicionConfirmModal (다건수신 의심 → 카톡 확인 후 후속 처리)
    showSuspicionModal,
    isSuspicionSaving,
    pendingSuspicion,
    handleSuspicionConfirmedSafe,
    handleSuspicionDelete,
    handleSuspicionResend,
    handleSuspicionClose,
    /** "잘못 전달 의심" 섹션에 별도 노출할 itemId 집합 */
    suspectedItemIds,
    /** 의심 섹션 행 탭 → SuspicionConfirmModal 재호출 */
    reopenSuspicionModal,

    showMemoModal,
    isMemoSaving,
    memoExistingValue,
    handleOpenMemoCoupon,
    handleOpenMemoDelivery,
    handleMemoSave,
    handleMemoClose,

    // PhoneLockEditModal
    showPhoneLockEdit,
    isPhoneLockSaving,
    phoneLockTarget,
    handleOpenPhoneLockEdit,
    handlePhoneLockSave,
    handleClosePhoneLockEdit,

    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  };
}
